import React, { useEffect, useState } from 'react';
import moment from 'moment';
import getJwtPayload from '../../utils/methods/getJwtPayload';
import { withTranslation } from '../Translator';
import { formatDate, formatToOptions } from '../../formatters';
import StatusBar from '../StatusBar';
import Button from '../Button';
import Stack from '../Stack';
import Text from '../Text';
import { ModalView } from '../ModalView';
import getContactLink from '../../utils/methods/getContactLink';
import getCheckoutLink from '../../utils/methods/getCheckoutLink';

const EUP_BANNER_CLOSED = 'pp_eup_traffic_monitor';
const DATA_TRAFFIC_USAGE_PREVENTION_API_URL = '/api/traffic-monitor/v1/usage-prevention';
const locationHostUrl = document.location.origin;

function getRequestConfig({ jwt }) {
  return {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
  };
}

function useEuPTrafficData({ jwt }) {
  const [data, setData] = useState({
    type: undefined,
    details: {
      threshold: undefined,
      deletion_at: undefined,
      suspension_at: undefined,
    },
  });

  useEffect(() => {
    fetch(DATA_TRAFFIC_USAGE_PREVENTION_API_URL, getRequestConfig({ jwt }))
      .then((resp) => {
        if (!resp.ok) {
          throw Error;
        }

        return resp.json();
      })
      .then((resp) => {
        setData(resp);
      })
      .catch(() => {});
  }, []);

  return data;
}

function getClosedBannerValue() {
  return localStorage.getItem(EUP_BANNER_CLOSED);
}

function setClosedBannerValue(close) {
  localStorage.setItem(EUP_BANNER_CLOSED, close);
}

function removeClosedBannerValue() {
  localStorage.removeItem(EUP_BANNER_CLOSED);
}

const calculateDaysDifference = (endDate) => {
  const currentTime = moment().utc().startOf('day');
  const suspensionTime = moment(endDate);
  const timerDiff = moment.duration(suspensionTime.diff(currentTime)).asDays().toFixed();

  return Math.max(0, timerDiff);
};

function getBannerConfig({ trafficInformation, threshold, suspensionAt, organizationName, t, languageForContactPage }) {
  const onLinkClick = () => window.open(getCheckoutLink({ organization: organizationName, language: languageForContactPage }), '_blank');
  const closedBannerValue = !!getClosedBannerValue();

  if (trafficInformation === 'traffic-warning') {
    return (
      closedBannerValue ? {}
        : {
          text: t('EuPBanner.notice.warning-notice-content.text', { threshold }),
          linkText: t('EuPBanner.notice.link-button.text'),
          close: true,
          type: 'warning',
          onLinkClick,
        });
  }

  if (trafficInformation !== undefined) removeClosedBannerValue();

  if (trafficInformation === 'soft-notice') {
    return ({
      text: t('EuPBanner.notice.soft-notice-content.text'),
      linkText: t('EuPBanner.notice.link-button.text'),
      close: false,
      type: 'error',
      onLinkClick,
    });
  }

  if (trafficInformation === 'hard-notice') {
    const count = calculateDaysDifference(suspensionAt);

    return ({
      text: t('EuPBanner.notice.hard-notice-content.text', { count }),
      linkText: t('EuPBanner.notice.link-button.text'),
      close: false,
      type: 'error',
      onLinkClick,
    });
  }

  return null;
}

function EuPNotifications({
  jwt,
  userRole,
  logout,
  userEmail,
  language,
  locationPathname = document.location.pathname,
  t,
} = {}) {
  const hideModal = locationPathname.includes('/subscription');
  const languageForContactPage = language.split('-')[0];
  const [bannerConfig, setBannerConfig] = useState({});
  const organizationName = getJwtPayload(jwt).org;
  const {
    type: trafficInformation,
    details: { threshold, deletion_at: deletionAt, suspension_at: suspensionAt },
  } = useEuPTrafficData({ jwt });

  useEffect(() => {
    setBannerConfig(getBannerConfig({
      trafficInformation,
      threshold,
      suspensionAt,
      organizationName,
      t,
      languageForContactPage,
    }));
  }, [trafficInformation]);

  if (deletionAt && !hideModal) {
    const deletionDateFormatted = formatDate(new Date(deletionAt), language, formatToOptions('L'));

    return (
      <ModalView
        title={t('EuPModal.modal.title')}
        actions={userRole === 'USER' ? (
          <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
            <Stack.Item>
              <Button
                text={t('EuPModal.button.logout')}
                onClick={logout}
              />
            </Stack.Item>
            <Stack.Item>
              <Button
                appearance="primary"
                text={t('EuPModal.button.buy')}
                onClick={() => {
                  window.open(getCheckoutLink({ organization: organizationName, language: languageForContactPage }), '_blank');
                }}
              />
            </Stack.Item>
          </Stack>
        )
          : (
            <Stack horizontalAlignment="fill">
              <Stack.Item>
                <Button
                  text={t('EuPModal.button.limit')}
                  onClick={() => {
                    window.open(`${locationHostUrl}/administration/account/subscription`, '_self');
                  }}
                />
              </Stack.Item>
              <Stack.Item>
                <Stack spacing="narrow">
                  <Stack.Item>
                    <Button
                      text={t('EuPModal.button.contact')}
                      onClick={() => {
                        window.open(
                          getContactLink({ language: languageForContactPage, organization: organizationName, email: userEmail, topic: 'higherLimit' }),
                          '_blank',
                        );
                      }}
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Button
                      appearance="primary"
                      text={t('EuPModal.button.buy')}
                      onClick={() => {
                        window.open(getCheckoutLink({ organization: organizationName, language: languageForContactPage }), '_blank');
                      }}
                    />
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          )}
      >
        <Text>
          {t(userRole === 'USER' ? 'EuPModal.information.user' : 'EuPModal.information.owner', { deletionDateFormatted })}
        </Text>
      </ModalView>
    );
  }

  if (!bannerConfig?.type) return null;

  return (
    <StatusBar
      text={bannerConfig.text}
      type={bannerConfig.type}
      linkText={bannerConfig.linkText}
      onLinkClick={bannerConfig.onLinkClick}
      linkType="button"
      onClose={() => {
        setBannerConfig(null);
        setClosedBannerValue(true);
      }}
      closeIcon={bannerConfig.close}
    />
  );
}

const enhanced = withTranslation('components')(EuPNotifications);

export { EuPNotifications as PureComponent };

export default enhanced;
